import { useCallback, useState } from "react";

const EPOCH_ISO = "1970-01-01T00:00:00.000Z";

function storageKey(userId: string) {
  return `vetnebula.chatLastRead:${userId}`;
}

function loadMap(userId: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveMap(userId: string, map: Record<string, string>) {
  localStorage.setItem(storageKey(userId), JSON.stringify(map));
}

function maxCreatedAt(messages: { created_at: string }[]): string {
  if (!messages.length) return new Date().toISOString();
  return messages.reduce(
    (latest, m) => (m.created_at > latest ? m.created_at : latest),
    messages[0].created_at,
  );
}

/**
 * Per-device last-read checkpoint per chat (ISO string). Used with unread aggregate query.
 */
export function useChatReadState(userId: string | undefined) {
  const [version, setVersion] = useState(0);

  const getLastReadIso = useCallback(
    (chatId: string) => {
      if (!userId) return EPOCH_ISO;
      const map = loadMap(userId);
      void version;
      return map[chatId] ?? EPOCH_ISO;
    },
    [userId, version],
  );

  const markChatRead = useCallback(
    (chatId: string, messages: { created_at: string }[]) => {
      if (!userId) return;
      const map = loadMap(userId);
      map[chatId] = maxCreatedAt(messages);
      saveMap(userId, map);
      setVersion((v) => v + 1);
    },
    [userId],
  );

  return {
    getLastReadIso,
    markChatRead,
    EPOCH_ISO,
    /** Bumps when last-read map changes; unread queries should refetch. */
    readStateVersion: version,
  };
}
